import { skipToken, useSuspenseQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { fromCents, printMoney } from '@guuru/money-common';
import GET_COMMUNITY_SETTINGS from './queries/GetPersistedCommunitySettings';

const getPartnerIdPath = () => {
  const { pathname } = window.location;
  const [, partnerId] = pathname.split('/');
  return partnerId;
};

const getPartnerIdParameter = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('partnerId') || undefined;
};

const useGetCurrentPartnerId = () => {
  const { partnerId } = useParams();
  if (partnerId) return partnerId;
  return getPartnerIdParameter() || getPartnerIdPath();
};

const useCommunitySettings = () => {
  const partnerId = useGetCurrentPartnerId();
  const {
    i18n: { language },
  } = useTranslation();
  const { data } = useSuspenseQuery(
    GET_COMMUNITY_SETTINGS,
    partnerId
      ? {
          variables: {
            id: partnerId,
            locale: language,
          },
        }
      : skipToken,
  );

  const { id, settings } = data?.community || {};
  const { name: partnerName, pricing } = settings || {};
  const isPoints = pricing?.[0]?.currency === 'XXX';

  const { goodAmount, partialAmount } = useMemo(
    () =>
      (pricing || []).reduce<Record<string, string | number>>((acc, item) => {
        if (!item?.amount) return acc;
        const { amount, currency, type } = item;
        const t = type.includes('good') ? 'goodAmount' : 'partialAmount';
        acc[t] = isPoints
          ? fromCents(amount)
          : printMoney(amount, currency, { isCents: true });
        return acc;
      }, {}),
    [pricing, isPoints],
  );

  return {
    id,
    ...settings,
    partnerId,
    partnerName,
    rewards: {
      isPoints,
      goodAmount,
      partialAmount,
    },
  };
};

export default useCommunitySettings;
